import { decodeProviderAccounts } from "./providerAccountInventory";
import type {
  CmmdClaudeRuntimeReadiness,
  CmmdOrgProviderPolicy,
  CmmdProviderAccountInventory,
  CmmdProviderAccountInventoryAccount,
  ProviderKind,
} from "@cmmd-forge/contracts";
import { WORKSPACE_PROVIDER_ACCESS_DISABLED } from "@cmmd-forge/shared/workspaceProviderAccess";
import { useQuery } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import {
  ForgeProviderAccessAuthorityError,
  resolveClaudeReadinessTargetId,
  resolveClaudeRuntimeReadiness,
} from "./claudeReadinessAccess";
import { createCmmdApiClient } from "../environments/cmmdApi";
import { isWalletAllowed } from "../providerPickerAvailability";
import {
  advanceClaudeReadinessPollingState,
  CLAUDE_READINESS_POLL_INTERVAL_MS,
  initialClaudeReadinessPollingState,
  shouldRefetchClaudeRuntimeReadiness,
  type ClaudeReadinessPollingSnapshot,
} from "./forgeProviderAccessPolling";

export type ProviderCredentialMode = "cmmd-cloud" | "runtime";

type PersonalProviderKind = Extract<ProviderKind, "codex" | "claudeAgent">;

export interface PersonalProviderConnections {
  readonly codex: boolean;
  readonly claudeAgent: boolean;
}

export interface PersonalProviderReconnectRequired {
  readonly codex: boolean;
}

export type ForgeProviderAccessState =
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | {
      readonly status: "ready";
      /** `null` only for standalone/local runtime mode, which has no CMMD dependency. */
      readonly policy: CmmdOrgProviderPolicy | null;
      readonly personalConnections: PersonalProviderConnections | null;
      readonly providerAccounts?: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
      /** Distinguishes an expired Codex credential from no saved connection. */
      readonly personalReconnectRequired?: PersonalProviderReconnectRequired;
      readonly claudeRuntimeReadiness: CmmdClaudeRuntimeReadiness | null;
    };

export {
  hasPotentialProviderAuthority,
  resolveProviderEligibility,
  resolveProvisioningProviderEligibility,
} from "./forgeProviderEligibility";
export type { ProviderEligibility, ProviderIneligibilityReason } from "./forgeProviderEligibility";

const NO_OFFERED_PROVIDER_POLICY: CmmdOrgProviderPolicy = {
  providers: { codex: false, claude: false, cmmd: false },
  // Fail closed: no provider is approved for metered egress until CMMD says so.
  meteredEgressApproved: { codex: false, claude: false, cmmd: false },
  allowedWallets: ["personal"],
  updatedAt: "1970-01-01T00:00:00.000Z",
};

// CMMD Admin is the sole cloud authority for offerability and personal/workspace wallet access.
// Standalone/local runtimes deliberately use their own executable inventory and runtime auth.

const FORGE_PROVIDER_ACCESS_QUERY_KEY = ["cmmd", "forge-provider-access"] as const;
const PROVIDER_ACCESS_REFETCH_INTERVAL_MS =
  import.meta.env.MODE === "test" ? 20 : CLAUDE_READINESS_POLL_INTERVAL_MS;

function shouldRetryProviderAccess(failureCount: number, error: Error): boolean {
  return !(error instanceof ForgeProviderAccessAuthorityError) && failureCount < 3;
}

export function forgeProviderAccessQueryKey(
  credentialMode: ProviderCredentialMode,
  readinessTargetId: string | null = null,
) {
  return [...FORGE_PROVIDER_ACCESS_QUERY_KEY, credentialMode, readinessTargetId] as const;
}

export async function updateForgeProviderConnectionState(input: {
  readonly queryClient: QueryClient;
  readonly provider: PersonalProviderKind;
  readonly connected: boolean;
}): Promise<void> {
  input.queryClient.setQueriesData<ForgeProviderAccessState>(
    { queryKey: FORGE_PROVIDER_ACCESS_QUERY_KEY },
    (current) => updateCachedPersonalConnection(current, input.provider, input.connected),
  );
  await input.queryClient.invalidateQueries({
    queryKey: FORGE_PROVIDER_ACCESS_QUERY_KEY,
    refetchType: "all",
  });
}

function updateCachedPersonalConnection(
  current: ForgeProviderAccessState | undefined,
  provider: PersonalProviderKind,
  connected: boolean,
): ForgeProviderAccessState | undefined {
  if (current?.status !== "ready" || current.personalConnections === null) return current;
  return {
    ...current,
    personalConnections: { ...current.personalConnections, [provider]: connected },
    ...(provider === "codex" ? { personalReconnectRequired: { codex: false } } : {}),
    ...(provider === "claudeAgent" ? { claudeRuntimeReadiness: null } : {}),
  };
}

export async function fetchForgeProviderAccess(
  credentialMode: ProviderCredentialMode,
  readinessTargetId: string | null = null,
): Promise<Extract<ForgeProviderAccessState, { status: "ready" }>> {
  if (credentialMode === "runtime") {
    return {
      status: "ready",
      policy: null,
      personalConnections: null,
      providerAccounts: [],
      claudeRuntimeReadiness: null,
    };
  }
  const client = createCmmdApiClient();
  const policy = decodeStrictOrgProviderPolicy(await client.getOrgProviderPolicy());
  if (isWalletAllowed("workspace", policy) && !WORKSPACE_PROVIDER_ACCESS_DISABLED) {
    return {
      status: "ready",
      policy,
      personalConnections: null,
      providerAccounts: [],
      claudeRuntimeReadiness: null,
    };
  }

  const [providerAccountsResult, codexConnection, claudeConnection, claudeRuntimeReadiness] =
    await Promise.all([
      resolveProviderAccounts(() => client.getProviderAccounts()),
      resolvePersonalConnection({
        enabled: policy.providers.codex,
        load: () => client.getUserCodexCredential(),
        provider: "codex",
      }),
      resolvePersonalConnection({
        enabled: policy.providers.claude,
        load: () => client.getUserClaudeCredential(),
        provider: "claudeAgent",
      }),
      resolveClaudeRuntimeReadiness({
        enabled: policy.providers.claude,
        readinessTargetId,
        load: (askedId) => client.getClaudeRuntimeReadiness(askedId),
      }),
    ]);
  const providerAccounts = providerAccountsResult.accounts.filter((account) =>
    account.provider === "codex" ? policy.providers.codex : policy.providers.claude,
  );
  const accountConnections = personalConnectionsFromAccounts(providerAccounts);
  const codexConnected = accountConnections.codex || codexConnection.connected;
  const claudeConnected = accountConnections.claudeAgent || claudeConnection.connected;
  const codexReconnectRequired =
    providerAccounts.some((account) => account.provider === "codex" && account.needsReconnect) ||
    codexConnection.reconnectRequired;
  return {
    status: "ready",
    policy,
    personalConnections: {
      codex: codexConnected,
      claudeAgent: claudeConnected,
    },
    providerAccounts,
    ...(codexReconnectRequired ? { personalReconnectRequired: { codex: true } } : {}),
    claudeRuntimeReadiness,
  };
}

function personalConnectionsFromAccounts(
  accounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>,
): PersonalProviderConnections {
  return {
    codex: accounts.some((account) => account.provider === "codex" && !account.needsReconnect),
    claudeAgent: accounts.some(
      (account) => account.provider === "claudeAgent" && !account.needsReconnect,
    ),
  };
}

export function resolveDefaultProviderAccountId(
  access: ForgeProviderAccessState,
  provider: PersonalProviderKind,
): string | undefined {
  if (access.status !== "ready") return undefined;
  const accounts = access.providerAccounts ?? [];
  const defaultAccount =
    accounts.find(
      (account) => account.provider === provider && account.isDefault && !account.needsReconnect,
    ) ?? accounts.find((account) => account.provider === provider && !account.needsReconnect);
  return defaultAccount?.providerAccountId;
}

async function resolveProviderAccounts(
  load: () => Promise<unknown>,
): Promise<CmmdProviderAccountInventory> {
  try {
    return decodeProviderAccounts(await load());
  } catch {
    return { accounts: [] };
  }
}

async function resolvePersonalConnection(input: {
  readonly enabled: boolean;
  readonly load: () => Promise<unknown>;
  readonly provider: PersonalProviderKind;
}): Promise<{ readonly connected: boolean; readonly reconnectRequired: boolean }> {
  if (!input.enabled) return { connected: false, reconnectRequired: false };
  try {
    return decodeConnectedState(await input.load(), input.provider);
  } catch {
    // Credential readiness is provider-scoped. A failed or malformed response
    // disables only that provider; authoritative policy failure remains the
    // global fail-closed boundary above.
    return { connected: false, reconnectRequired: false };
  }
}

function claudeReadinessPollingSnapshot(
  data: Extract<ForgeProviderAccessState, { status: "ready" }> | undefined,
): ClaudeReadinessPollingSnapshot {
  const claudeOffered = data?.policy?.providers.claude === true;
  const claudeConnected = data?.personalConnections?.claudeAgent === true;
  return {
    claudeOffered,
    claudeConnected,
    deliveryStatus: data?.claudeRuntimeReadiness?.deliveryStatus ?? null,
    reason: data?.claudeRuntimeReadiness?.reason ?? null,
    readinessUnavailable: claudeOffered && claudeConnected && data?.claudeRuntimeReadiness === null,
  };
}

export function useForgeProviderAccess(input: {
  readonly enabled: boolean;
  readonly credentialMode: ProviderCredentialMode;
  /** The direct legacy answer. Callers that hold an environment id pass that instead. */
  readonly runtimeId?: string | null;
  /**
   * Preferred: the readiness ask is resolved from the environment id through
   * `resolveClaudeReadinessTargetId`, so every cloud shape keys the query,
   * durable `env_` Environments included.
   */
  readonly environmentId?: string | null;
}): ForgeProviderAccessState {
  // One resolution feeding one query key: the environment id wins when the
  // caller holds one, and only a direct runtime id survives as the fallback.
  const readinessTargetId =
    input.environmentId != null
      ? resolveClaudeReadinessTargetId(input.environmentId)
      : (input.runtimeId ?? null);
  const readinessPollingRef = useRef(initialClaudeReadinessPollingState(readinessTargetId));
  const query = useQuery({
    queryKey: forgeProviderAccessQueryKey(input.credentialMode, readinessTargetId),
    queryFn: () => fetchForgeProviderAccess("cmmd-cloud", readinessTargetId),
    enabled: input.enabled && input.credentialMode === "cmmd-cloud",
    retry: shouldRetryProviderAccess,
    staleTime: 30_000,
    refetchInterval: (currentQuery) => {
      if (currentQuery.state.error instanceof ForgeProviderAccessAuthorityError) return false;
      const snapshot = claudeReadinessPollingSnapshot(currentQuery.state.data);
      readinessPollingRef.current = advanceClaudeReadinessPollingState({
        current: readinessPollingRef.current,
        readinessTargetId,
        dataUpdatedAt: currentQuery.state.dataUpdatedAt,
        readinessUnavailable: readinessTargetId !== null && snapshot.readinessUnavailable,
        deliveryStatus: snapshot.deliveryStatus,
      });
      return readinessTargetId !== null &&
        shouldRefetchClaudeRuntimeReadiness(snapshot, readinessPollingRef.current)
        ? PROVIDER_ACCESS_REFETCH_INTERVAL_MS
        : false;
    },
  });
  if (!input.enabled) return { status: "loading" };
  if (input.credentialMode === "runtime") {
    return {
      status: "ready",
      policy: null,
      personalConnections: null,
      providerAccounts: [],
      claudeRuntimeReadiness: null,
    };
  }
  if (query.isPending) return { status: "loading" };
  if (query.isError || !query.data) return { status: "error" };
  return query.data;
}

function decodeStrictOrgProviderPolicy(value: unknown): CmmdOrgProviderPolicy {
  if (!isRecord(value) || !isRecord(value.providers)) {
    throw new Error("CMMD returned an invalid organization provider policy.");
  }
  const { providers, meteredEgressApproved, allowedWallets, updatedAt } = value;
  const metadata = decodeStrictPolicyMetadata(allowedWallets, updatedAt);
  if (!hasStrictProviderFlags(providers) || metadata === null) {
    throw new Error("CMMD returned an invalid organization provider policy.");
  }
  return {
    providers: { codex: providers.codex, claude: providers.claude, cmmd: providers.cmmd },
    // Additive: an older cached response or malformed value fails closed
    // rather than throwing, since nothing reads this field yet.
    meteredEgressApproved: decodeProviderFlags(meteredEgressApproved),
    allowedWallets: [...new Set(metadata.allowedWallets)],
    updatedAt: metadata.updatedAt,
  };
}

/** Decodes `{ codex, claude, cmmd }` booleans, or fails closed to all-false. */
function decodeProviderFlags(value: unknown): { codex: boolean; claude: boolean; cmmd: boolean } {
  return isRecord(value) && hasStrictProviderFlags(value)
    ? { codex: value.codex, claude: value.claude, cmmd: value.cmmd }
    : { codex: false, claude: false, cmmd: false };
}

function hasStrictProviderFlags(providers: Record<string, unknown>): providers is Record<
  string,
  unknown
> & {
  readonly codex: boolean;
  readonly claude: boolean;
  readonly cmmd: boolean;
} {
  return (
    typeof providers.codex === "boolean" &&
    typeof providers.claude === "boolean" &&
    typeof providers.cmmd === "boolean"
  );
}

function decodeStrictPolicyMetadata(
  allowedWallets: unknown,
  updatedAt: unknown,
): {
  readonly allowedWallets: ReadonlyArray<"personal" | "workspace">;
  readonly updatedAt: string;
} | null {
  const valid =
    Array.isArray(allowedWallets) &&
    allowedWallets.length > 0 &&
    allowedWallets.every(isProviderWallet) &&
    typeof updatedAt === "string" &&
    updatedAt.trim().length > 0;
  return valid
    ? {
        allowedWallets: allowedWallets as ReadonlyArray<"personal" | "workspace">,
        updatedAt: updatedAt as string,
      }
    : null;
}

function decodeConnectedState(
  value: unknown,
  provider: PersonalProviderKind,
): { readonly connected: boolean; readonly reconnectRequired: boolean } {
  if (!isRecord(value) || typeof value.connected !== "boolean") {
    throw new Error(`CMMD returned an invalid ${provider} connection state.`);
  }
  if (!value.connected) return { connected: false, reconnectRequired: false };
  if (provider === "codex") {
    if (typeof value.needsReconnect !== "boolean") {
      throw new Error("CMMD returned an invalid Codex connection state.");
    }
    return { connected: !value.needsReconnect, reconnectRequired: value.needsReconnect };
  }
  if (typeof value.generationId !== "string" || value.generationId.trim().length === 0) {
    throw new Error("CMMD returned an invalid Claude connection state.");
  }
  return { connected: true, reconnectRequired: false };
}

function isProviderWallet(value: unknown): value is "personal" | "workspace" {
  return value === "personal" || value === "workspace";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function providerPolicyForAccess(
  access: ForgeProviderAccessState,
): CmmdOrgProviderPolicy | null {
  return access.status === "ready" ? access.policy : NO_OFFERED_PROVIDER_POLICY;
}
